// Centralized API base URL for frontend -> backend calls
// Now sourced from config to avoid env var issues on Vercel.
import { API_BASE } from './config';
import toast from './utils/toast';

const TOKEN_KEY = 'lrbrain-site-token';
const ROLE_KEY = 'lrbrain-user-role';
const EMAIL_KEY = 'lrbrain-user-email';
const USER_KEY = 'lrbrain-user';

export function apiUrl(path) {
  // Ensure single slash between base and path
  if (!path.startsWith('/')) path = `/${path}`;
  const runtimeApiBase = (() => {
    if (API_BASE) return API_BASE;
    if (typeof window === 'undefined') return '';
    const host = window.location?.hostname;
    const port = window.location?.port;
    if ((host === 'localhost' || host === '127.0.0.1') && port && port !== '5000') {
      return 'http://localhost:5000';
    }
    return '';
  })();
  if (!runtimeApiBase) return path;
  return `${runtimeApiBase}${path}`;
}

/**
 * Parse retry-after header value
 * @param {Response} response - Fetch response object
 * @returns {number|null} - Seconds to wait, or null if not present
 */
function parseRetryAfter(response) {
  const retryAfter = response.headers.get('Retry-After');
  if (!retryAfter) return null;

  // Check if it's a number (seconds)
  const seconds = parseInt(retryAfter, 10);
  if (!isNaN(seconds)) return seconds;

  // Check if it's a date string
  const date = new Date(retryAfter);
  if (!isNaN(date.getTime())) {
    const now = Date.now();
    const diff = Math.ceil((date.getTime() - now) / 1000);
    return diff > 0 ? diff : null;
  }

  return null;
}

function parseRateLimitPayload(response) {
  try {
    const clone = response.clone();
    return clone.json().catch(() => null);
  } catch {
    return Promise.resolve(null);
  }
}

export function apiFetch(path, options = {}) {
  // Get auth token from localStorage
  // SECURITY: Role is now embedded in the cryptographically signed token,
  // NOT sent as a separate header. This prevents privilege escalation attacks
  // where users could modify localStorage to gain admin access.
  const token = localStorage.getItem(TOKEN_KEY);

  // Merge headers with Authorization only (role is in token)
  const headers = { ...options.headers };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  // NOTE: X-LRBrain-Role header removed - role is now cryptographically
  // signed into the token and extracted by the backend middleware

  return fetch(apiUrl(path), { ...options, headers }).then(response => {
    // If we get a 401, clear the token and reload to show password gate
    if (response.status === 401) {
      localStorage.removeItem(TOKEN_KEY);
      window.location.reload();
      return response;
    }

    // Handle 429 - show rate limit toast
    if (response.status === 429) {
      const retryAfter = parseRetryAfter(response);
      parseRateLimitPayload(response).then((payload) => {
        toast.rateLimit(retryAfter, payload?.limit_scope || 'app_rate_limit');
      }).catch(() => {
        toast.rateLimit(retryAfter, 'app_rate_limit');
      });
      // Still return the response so caller can handle if needed
    }

    return response;
  });
}

/**
 * Explicit AI fetch helper.
 * Adds required user-action header for backend AI guardrails.
 */
export function apiAIFetch(path, action, options = {}) {
  const aiHeaders = {
    ...(options.headers || {}),
    'X-LRB-User-Action': action,
  };
  return apiFetch(path, { ...options, headers: aiHeaders });
}

/**
 * Parse API response safely across JSON and non-JSON payloads.
 * Returns a normalized shape to prevent JSON.parse crashes in callers.
 */
export async function parseApiResponse(response, context = 'api') {
  const contentType = (response.headers.get('Content-Type') || '').toLowerCase();
  let raw = '';
  let data = null;

  try {
    raw = await response.text();
  } catch {
    raw = '';
  }

  if (raw) {
    const shouldParseJson = contentType.includes('application/json');
    if (shouldParseJson) {
      try {
        data = JSON.parse(raw);
      } catch {
        data = null;
      }
    } else {
      // Try JSON anyway in case the backend forgot to set content-type.
      try {
        data = JSON.parse(raw);
      } catch {
        data = null;
      }
    }
  }

  if (!response.ok) {
    if (!contentType.includes('application/json')) {
      console.warn(`[api:${context}] Non-JSON error response`, {
        status: response.status,
        contentType: contentType || 'unknown'
      });
    }

    const message = (
      (data && (data.error || data.message)) ||
      (raw && raw.trim()) ||
      `Request failed (${response.status})`
    );

    return {
      ok: false,
      status: response.status,
      data,
      error: {
        code: data?.code || null,
        status: response.status,
        message,
        raw
      }
    };
  }

  return {
    ok: true,
    status: response.status,
    data: data || {}
  };
}

/**
 * Helper to create an SSE-compatible fetch with 429 handling
 * For streaming endpoints that can't use apiFetch
 */
export function createStreamFetch(path, options = {}) {
  // SECURITY: Role is embedded in the signed token, not sent as separate header
  const token = localStorage.getItem(TOKEN_KEY);
  const headers = {
    ...options.headers,
    ...(token && { 'Authorization': `Bearer ${token}` }),
    // X-LRBrain-Role header removed - role is in signed token
  };

  return fetch(apiUrl(path), { ...options, headers }).then(response => {
    if (response.status === 401) {
      localStorage.removeItem(TOKEN_KEY);
      window.location.reload();
    }

    if (response.status === 429) {
      const retryAfter = parseRetryAfter(response);
      toast.rateLimit(retryAfter);
    }

    return response;
  });
}

// ============================================================================
// Role Management Helper Functions
// ============================================================================

/**
 * Set the user's role and optionally email in localStorage
 * @param {string} role - The role (admin, team_captain, student)
 * @param {string|null} email - Optional email address
 */
export function setUserRole(role, email = null) {
  localStorage.setItem(ROLE_KEY, normalizeRole(role));
  if (email) {
    localStorage.setItem(EMAIL_KEY, email);
  }
}

/**
 * Get the current user's role from localStorage
 * @returns {string|null} The role or null if not set
 */
export function getUserRole() {
  const storedRole = localStorage.getItem(ROLE_KEY);
  const normalizedRole = normalizeRole(storedRole);
  if (storedRole !== normalizedRole && normalizedRole) {
    localStorage.setItem(ROLE_KEY, normalizedRole);
  }
  return normalizedRole;
}

/**
 * Get the current user's email from localStorage
 * @returns {string|null} The email or null if not set
 */
export function getUserEmail() {
  return localStorage.getItem(EMAIL_KEY);
}

/**
 * Clear all user session data (token, role, email, user)
 * Used when logging out or session expires
 */
export function clearUserSession() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(ROLE_KEY);
  localStorage.removeItem(EMAIL_KEY);
  localStorage.removeItem(USER_KEY);
}

// ============================================================================
// Google OAuth User Functions
// ============================================================================

/**
 * Store user object from Google OAuth login
 * @param {Object} user - User object with email, display_name, role, profile_id
 */
export function setStoredUser(user) {
  if (user) {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    // Also set individual keys for backward compatibility
    if (user.role) {
      localStorage.setItem(ROLE_KEY, normalizeRole(user.role));
    }
    if (user.email) {
      localStorage.setItem(EMAIL_KEY, user.email);
    }
  }
}

/**
 * Get stored user object from localStorage
 * @returns {Object|null} User object or null if not stored
 */
export function getStoredUser() {
  try {
    const userStr = localStorage.getItem(USER_KEY);
    return userStr ? JSON.parse(userStr) : null;
  } catch {
    return null;
  }
}

/**
 * Get auth token from localStorage
 * @returns {string|null} Token or null if not stored
 */
export function getAuthToken() {
  return localStorage.getItem(TOKEN_KEY);
}

/**
 * Set auth token in localStorage
 * @param {string} token - The authentication token
 */
export function setAuthToken(token) {
  localStorage.setItem(TOKEN_KEY, token);
}
const normalizeRole = (role) => {
  const normalized = String(role || '').trim().toLowerCase();
  if (normalized === 'brainiac') return 'student';
  return normalized;
};
